// Utility to read Tailwind config values
// This works in both Node.js and browser environments

// For browser environments, we'll define the config structure manually
// based on your actual tailwind.config.cjs file
const browserTailwindConfig = {
  prefix: 'cr-',
  theme: {
    fontFamily: {
      main: 'Poppins, sans-serif',
      secondary: 'Poppins, sans-serif',
      header: 'Zen Dots, sans-serif',
      tooltip: 'Poppins, Arial, sans-serif',
      mui: 'Poppins, Helvetica',
    },
    screens: {
      xs: '380px',
      sm: '480px',
      md: '768px',
      lg: '1024px',
      xl: '1440px',
    },
    extend: {
      colors: {
        cirrusBlue: {
          10: '#E8F8FE',
          25: '#DEF1F9',
          35: '#CCDDE5',
          50: '#CAE2EA',
          75: '#BBD5DE',
          100: '#B0C9D1',
          400: '#03365D',
        },
        transparent: 'transparent',
        blue: {
          50: '#e9f3ff',
          100: '#d4e5fc',
          200: '#acd6f8',
          300: '#60a5de',
          400: '#2e85ce',
          500: '#1879B9',
          600: '#4E8EA5',
          800: '#09487C',
          900: '#03365D',
        },
        green: {
          50: '#edfaf4',
          100: '#def8eb',
          200: '#a7f1d1',
          500: '#00B566',
          600: '#00B49E',
          900: '#048876',
        },
        purple: '#7e5bef',
        pink: '#ff49db',
        red: '#f86464',
        orange: '#ff7849',
        yellow: '#ffc82c',
        'gray-dark': '#273444',
        gray: {
          100: '#f7f7f7',
          300: '#cacaca',
          500: '#8492a6',
          600: '#959595',
          800: '#656565',
        },
        'gray-light': '#d3dce6',
        white: '#fff',
        neutral: { 100: '#f5f5f5' },
      },
      fontSize: {
        md: '.875rem',
      },
      spacing: {
        '8xl': '96rem',
        '9xl': '128rem',
      },
      zIndex: {
        '90': 90,
        '5000': 5000,
        '99': 99,
        '999': 999,
        '9999': 9999,
      },
      borderRadius: {
        '4xl': '2rem',
      },
    },
  },
};

/**
 * Get Tailwind config - works in both Node.js and browser environments
 */
export const getTailwindConfig = () => {
  // Try to use Node.js require if available (development)
  if (typeof require !== 'undefined') {
    try {
      return require('../../tailwind.config.cjs');
    } catch (error) {
      console.warn('Could not load tailwind.config.cjs, using browser config');
    }
  }

  // Fallback to browser config
  return browserTailwindConfig;
};

/**
 * Get colors from Tailwind config
 */
export const getColors = () => {
  const config = getTailwindConfig();
  return config.theme?.extend?.colors || {};
};

/**
 * Get font families from Tailwind config
 */
export const getFontFamilies = () => {
  const config = getTailwindConfig();
  return config.theme?.fontFamily || {};
};

/**
 * Get screen breakpoints from Tailwind config
 */
export const getScreens = () => {
  const config = getTailwindConfig();
  return config.theme?.screens || {};
};

/**
 * Get the Tailwind prefix
 */
export const getPrefix = () => {
  const config = getTailwindConfig();
  return config.prefix || '';
};

/**
 * Get a specific color value
 */
export const getColor = (colorName: string, shade?: string | number) => {
  const colors = getColors();
  const color = colors[colorName];

  if (typeof color === 'string') return color;
  if (color && typeof color === 'object' && shade) {
    return color[shade] || color;
  }
  return color;
};

/**
 * Get a specific font family
 */
export const getFontFamily = (fontName: string) => {
  const fonts = getFontFamilies();
  return fonts[fontName];
};

/**
 * Get a specific screen breakpoint
 */
export const getScreen = (screenName: string) => {
  const screens = getScreens();
  return screens[screenName];
};

/**
 * Get font sizes from Tailwind config
 */
export const getFontSizes = () => {
  const config = getTailwindConfig();
  return config.theme?.extend?.fontSize || {};
};

/**
 * Get spacing values from Tailwind config
 */
export const getSpacing = () => {
  const config = getTailwindConfig();
  return config.theme?.extend?.spacing || {};
};

/**
 * Get z-index values from Tailwind config
 */
export const getZIndex = () => {
  const config = getTailwindConfig();
  return config.theme?.extend?.zIndex || {};
};

/**
 * Get border radius values from Tailwind config
 */
export const getBorderRadius = () => {
  const config = getTailwindConfig();
  return config.theme?.extend?.borderRadius || {};
};
